import { EventListPageProps } from "/Models/Generated/EventListPageProps";
import "./EventListPage.scss";
import {
  GridRow,
  HeroBasic,
  GridPresetWideList,
  Pagination,
  EventListItemGroup,
  Tabs,
  Button,
  ToastMessage,
  LoadingOverlay,
  AriaStatus,
} from "/App.Components";
import EventListClient, { EventListResults } from "./EventListClient";
import { BaseListFilters } from "/Helpers/Hooks/UseListPageFilters";
import useListPageFilters from "/Helpers/Hooks/UseListPageFilters";
import { EventListItemGroupProps } from "/Models/Generated/EventListItemGroupProps";
import { useEffect, useRef, useState } from "react";
import useScrollTopTracker from "/Helpers/Hooks/useScrollTopTracker";
import useAriaUpdateHook from "/Helpers/Hooks/useAriaUpdateHook";

export interface EventListFilters extends BaseListFilters {
  pageNumber?: number;
  upcomingEvents?: boolean;
}

const EventListPage = ({
  pageHeader,
  tabs,
  eventList,
  endpoint,
  language,
  noResultsMessage,
  newsletterSignupText,
  pagination,
  baseUrl,
}: EventListPageProps) => {
  const client = new EventListClient(
    endpoint,
    language,
    eventList?.eventListPageId ?? 0,
    baseUrl,
  );
  const {
    items,
    resultsCount,
    error,
    loading,
    filters,
    totalPages,
    setError,
    setPageNumber,
    setTab,
  } = useListPageFilters({
    client,
    initList: eventList as EventListResults,
    initFilters: {
      pageNumber: eventList?.currentPageNumber,
      upcomingEvents: tabs?.upcomingEventsTabSelected ?? true,
    },
  });

  const [doScroll, setDoScroll] = useState(false);
  const scrollToRef = useRef<HTMLDivElement>(null);
  const scrollTop = useScrollTopTracker(scrollToRef);

  useEffect(() => {
    if (loading) return;
    if (!doScroll) return;
    setDoScroll(false);
    window.scrollTo({
      top: scrollTop,
      behavior: "smooth",
    });
  }, [loading]);

  const { ariaStatus } = useAriaUpdateHook(resultsCount);

  return (
    <div className="event-list-page-norad">
      <ToastMessage
        message={error ?? ""}
        visible={!!error}
        onClose={() => setError("")}
      />
      <LoadingOverlay loading={loading} />
      {pageHeader && <HeroBasic {...pageHeader} />}
      <div className="event-list-page-norad__content" ref={scrollToRef}>
        <AriaStatus message={ariaStatus} />
        {tabs && (
          <GridRow>
            <GridPresetWideList>
              <Tabs
                {...tabs}
                onTabClick={(upComing) => setTab(upComing)}
                upcomingEventsTabSelected={
                  (filters as EventListFilters)?.upcomingEvents ?? true
                }
              />
            </GridPresetWideList>
          </GridRow>
        )}

        {items ? (
          <ol className="event-list-page-norad__list">
            {(items as EventListItemGroupProps[]).map((group, idx) => (
              <EventListItemGroup key={"item-group" + idx} {...group} />
            ))}
          </ol>
        ) : (
          <GridRow>
            <GridPresetWideList>
              <div className="event-list-page-norad__no-results">
                <h2 className="event-list-page-norad__no-results-message">
                  {noResultsMessage}
                </h2>
                {newsletterSignupText && (
                  <Button style={"primary"} label={newsletterSignupText} /> //<--todo
                )}
              </div>
            </GridPresetWideList>
          </GridRow>
        )}
        {items && (
          <div className="event-list-page-norad__pagination">
            <Pagination
              {...pagination}
              currentPage={filters.pageNumber ?? 1}
              setCurrentPage={(pageNum) => {
                setPageNumber(
                  pageNum,
                  (filters as EventListFilters).upcomingEvents,
                );
                setDoScroll(true);
              }}
              totalPages={totalPages ?? 1}
              nextHrefForCrawlers={
                (filters.pageNumber ?? totalPages ?? 1 < (totalPages ?? 1))
                  ? client.BuildUrlFromProps({
                      pageNumber: (filters.pageNumber ?? 1) + 1,
                      upcomingEvents: tabs?.upcomingEventsTabSelected ?? true,
                    })
                  : undefined
              }
              previousHrefForCrawlers={
                (filters.pageNumber ?? 1) > 1
                  ? client.BuildUrlFromProps({
                      pageNumber: (filters.pageNumber ?? 1) - 1,
                      upcomingEvents: tabs?.upcomingEventsTabSelected ?? true,
                    })
                  : undefined
              }
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EventListPage;
